import { addDays } from 'date-fns';
import { FisReportRequestDto } from '@swagger/humanresources';
import { berlinDayParts, IN_BERLIN, toBerlin } from '@shared/berlin-time';
import {
  FisReportRequest,
  ReportRowError,
  ReportTask,
  ReportTaskRow,
  ReportTaskStatus,
  rowErrorText,
  StoredReportRequest,
  toReportTaskStatus,
} from '@features/users/util-reports';

const OU_SEPARATOR = ';';
/** `uid*` loads the OU with its descendants; a bare uid only the OU itself. */
const WITH_DESCENDANTS = '*';

/**
 * The backend wants UTC instants of Berlin midnight: `start` inclusive,
 * `stop` the midnight after the last day. A day key read by `toBerlin` is
 * Berlin midnight of that day (#5248).
 */
export function toFisReportRequestDto(request: FisReportRequest): FisReportRequestDto {
  const start = toBerlin(request.startDay);
  const stopExclusive = addDays(toBerlin(request.stopDay), 1, IN_BERLIN);
  return {
    reports: request.reports,
    start: start.toISOString(),
    stop: stopExclusive.toISOString(),
    ou: request.branchUIds.length ? toOuFilter(request.branchUIds) : null,
    // No switch in the UI (#5248): the legacy "Freizeit" rule is the only one offered.
    freizeitAsWorkDiff: false,
  };
}

/**
 * A branch report covers the staff of the branch's departments and planning
 * units too, like "Alle" does; the stored request keeps the bare uIds (#5248).
 */
function toOuFilter(branchUIds: readonly string[]): string {
  return branchUIds.map((uId) => `${uId}${WITH_DESCENDANTS}`).join(OU_SEPARATOR);
}

/** `statusOverride`: what SignalR or a download told the app after the list was loaded. */
export function toReportTaskRow(
  task: ReportTask,
  stored: StoredReportRequest | undefined,
  statusOverride: ReportTaskStatus | undefined,
  rowError: ReportRowError | undefined,
): ReportTaskRow {
  const status = statusOverride ?? toReportTaskStatus(task.latestMilestone);
  return {
    uId: task.uId,
    name: task.name ?? null,
    reportKinds: stored?.request.reports ?? [],
    branchKeys: stored?.branchKeys ?? null,
    periodLabel: stored ? toPeriodLabel(stored.request) : null,
    createdAt: task.created ?? stored?.createdAt ?? '',
    status,
    rowError: rowErrorText(rowError, status),
    canRestart: !!stored,
  };
}

/** `01.08.–31.08.2026`, or `01.12.2025–31.01.2026` across years. */
export function toPeriodLabel(request: FisReportRequest): string {
  const start = berlinDayParts(request.startDay);
  const stop = berlinDayParts(request.stopDay);
  const startLabel =
    start.year === stop.year ? dayMonth(start.day, start.monthIndex) : dayMonthYear(start);
  return `${startLabel}–${dayMonthYear(stop)}`;
}

function dayMonth(day: number, monthIndex: number): string {
  return `${pad2(day)}.${pad2(monthIndex + 1)}.`;
}

function dayMonthYear(parts: { day: number; monthIndex: number; year: number }): string {
  return `${dayMonth(parts.day, parts.monthIndex)}${parts.year}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
